import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { ComponentPortal, Portal } from '@angular/cdk/portal';
import { WebSpeechApiService } from '../library/web-speech-api.service';
import { ControlService } from '../library/control.service';
import { AppConstants } from 'src/app/app-constants';
import { UserVideoService } from '../library/user-video.service';
import { DeviceSettingsService } from '../library/device-settings.service';
import { DeviceSettingsSpeechRecognitionComponent } from '../device-settings/device-settings-speech-recognition/device-settings-speech-recognition.component';
import { Subscription } from 'rxjs';
import { PlayerService } from '../library/player.service';
import { ICallVisualAid } from 'src/app/interfaces/icall-visual-aid';
import { NetworkNavigator } from 'src/app/interfaces/inetwork-navigator';
import { CallStoreService } from '../library/call-store/call-store.service';
import { LoggerService } from 'src/app/services/logger/logger.service';

@Component({
  selector: 'app-call-visual-aid-v2',
  templateUrl: './call-visual-aid-v2.component.html',
  styleUrls: ['./call-visual-aid-v2.component.scss'],
  standalone: false
})
export class CallVisualAidComponentV2 implements OnInit, OnDestroy {
  @Output() visualAidClicked = new EventEmitter<void>();
  @Output() currentPageChanged = new EventEmitter<number>();

  @Input() webSpeechApiService: WebSpeechApiService | undefined;
  @Input() controlService: ControlService | undefined;
  @Input() userVideoService: UserVideoService | undefined;
  @Input() deviceSettingsService: DeviceSettingsService | undefined;
  @Input() playerService: PlayerService | undefined;
  @Input() practiceCallUuid: string = '';

  advanceTypeManual: string = AppConstants.ADVANCE_TYPE_MANUAL;
  advanceTypeAuto: string = AppConstants.ADVANCE_TYPE_AUTOMATIC;
  errorSubscription!: Subscription;
  selectedPortal: Portal<any> | null = null;
  componentPortal: ComponentPortal<DeviceSettingsSpeechRecognitionComponent> | null =
    null;
  visualAidDisabled: boolean = true;
  imgArray: string[] = [];
  preloadedImages: HTMLImageElement[] = [];
  imageExtensions: string[] = ['.jpeg', '.jpg', '.png', '.gif'];
  currentPage: number = 0;
  visualAid: ICallVisualAid | undefined;
  private loadedImageIndices: Set<number> = new Set();

  constructor(
    private callStore: CallStoreService,
    private logger: LoggerService
  ) {}

  async ngOnInit(): Promise<void> {
    if (this.controlService) {
      this.callStore.setDisplayPostCall(false);
      this.visualAid = this.callStore.getCall()?.visualAid;
      this.preloadImages();
      const display = this.controlService.shouldDisplayTour();
      if (display) {
        this.controlService.displayTour = true;
      }
    }
    // Subscribing to error message, waiting for the error that can emit this in the webSpeechApiService logic
    if (this.webSpeechApiService) {
      this.errorSubscription = this.webSpeechApiService
        .getError()
        .subscribe(() => {
          if (this.componentPortal === null) {
            this.componentPortal = new ComponentPortal(
              DeviceSettingsSpeechRecognitionComponent
            );
          }
          this.selectedPortal = this.componentPortal;
        });
    }

    window.addEventListener('online', () => {
      console.log('Network reconnected, retrying missing images...');
      if (this.visualAid) {
        const missingIndices = this.getMissingImageIndices(
          Number(this.visualAid.fileCount)
        );
        this.preloadMissingImages(missingIndices);
      }
    });

  }

  ngOnDestroy(): void {
    if (this.errorSubscription) {
      this.errorSubscription.unsubscribe();
    }
    // remove speech recognition validation interval
    if (this.controlService) {
      this.playerService?.clearTranscripts();
      if (this.controlService.intervalSpeechDetected) {
        clearInterval(this.controlService.intervalSpeechDetected);
      }
    }
  }

  ngAfterViewInit(): void {
    this.preloadImages();
  }

  onDisplayVisualAidButtonClick() {
    this.visualAidClicked.emit(); // Emit event to ChildComponent
  }

  preloadImages(): void {
    this.checkConnectivity();
    if (navigator.onLine) {
      if (
        !this.visualAid ||
        !this.visualAid?.fileCount ||
        !this.visualAid?.baseUrl
      ) {
        console.error('Visual aid or file count or base URL is not defined.');
        this.logVisualAidError(
          'Visual aid or file count or baseurl is not defined.'
        );
        return;
      }

      for (let i = 0; i < Number(this.visualAid.fileCount); i++) {
        const baseSrc = `${this.visualAid.baseUrl}${i + 1}`;
        this.loadImageWithExtensions(baseSrc, i + 1);
      }
    }
  }

  loadImageWithExtensions(baseSrc: string, index: number): void {
    let attempts = 0;
    const tryNextExtension = (): void => {
      if (attempts < this.imageExtensions.length) {
        const src = baseSrc + this.imageExtensions[attempts];
        attempts++;
        this.loadImage(src, tryNextExtension, index);
      } else {
        console.error(`Image ${index} failed to load with any extension.`);
        this.logVisualAidError(
          `Image ${index} failed to load with any extension.`
        );
      }
    };

    tryNextExtension();
  }

  loadImage(src: string, onError: () => void, index: number): void {
    console.log('loadedImageIndices', this.loadedImageIndices);
    if (this.loadedImageIndices.has(index)) {
      console.warn(`Skipping duplicate load for Image ${index}`);
      return;
    }
    const img = new Image();
    img.src = src;

    img.onload = () => {
      if (!this.loadedImageIndices.has(index)) {
        this.imgArray.push(src);
        this.preloadedImages.push(img);
        this.loadedImageIndices.add(index);
        const extensionMatch = src.match(/\.(jpeg|jpg|png|gif)$/);
        const extension = extensionMatch ? extensionMatch[1] : 'jpeg';
        console.log('selected extension:', extension);
        this.imgArray = this.sortImagesByNumber(this.imgArray, extension);
        console.log(this.imgArray.length + ' image(s) loaded');
        console.log(
          `Image ${index} loaded. Total: ${this.preloadedImages.length} images.`
        );
      }
    };

    img.onerror = () => {
      console.error(`Failed to load Image ${index}: ${src}`);
      this.logVisualAidError(`Failed to load Image ${index}: ${src}`);
      onError();
    };
  }

  sortImagesByNumber(imgArray: string[], extension: string): string[] {
    return imgArray.sort((a, b) => {
      const extractNumber = (url: string): number => {
        const regex = new RegExp(`(\\d+)(?=\\.${extension})`);
        const match = url.match(regex);
        return match ? parseInt(match[0], 10) : 0;
      };
      const numA = extractNumber(a);
      const numB = extractNumber(b);

      return numA - numB;
    });
  }

  canDisplayVisualAid() {
    if (
      !this.visualAid ||
      !this.visualAid?.fileCount ||
      !this.visualAid?.baseUrl
    ) {
      this.logVisualAidError(
        'Visual aid or file count or baseurl is not defined.'
      );
      return false;
    }

    const totalImages = Number(this.visualAid.fileCount);
    const missingIndices = this.getMissingImageIndices(totalImages);
    if (missingIndices.length > 0) {
      console.warn(
        `Missing ${missingIndices.length} images. Displaying error banner.`
      );
      this.preloadMissingImages(missingIndices);
      return false;
    }

    console.log('All images loaded, hiding error banner.');
    return true;
  }

  getMissingImageIndices(totalImages: number): number[] {
    const expectedIndices = new Set(
      [...Array(totalImages).keys()].map((i) => i + 1)
    );
    console.log('expected indices', expectedIndices);
    const loadedIndices = new Set(this.loadedImageIndices);
    console.log('loadedIndices', loadedIndices);
    return [...expectedIndices].filter((index) => !loadedIndices.has(index));
  }

  preloadMissingImages(missingIndices: number[]): void {
    this.checkConnectivity();
    if (navigator.onLine) {
      missingIndices.forEach((index) => {
        const baseSrc = `${this.visualAid?.baseUrl}${index}`;
        this.loadImageWithExtensions(baseSrc, index);
      });
    }
  }

  checkConnectivity() {
    const networkNavigator = navigator as NetworkNavigator;
    if (networkNavigator.connection?.effectiveType) {
      console.log(
        `Network speed detected: ${networkNavigator.connection.effectiveType}`
      );
      if (
        ['slow-2g', '2g', '3g', 'slow-4g'].includes(
          networkNavigator.connection.effectiveType
        )
      ) {
        console.warn('Slow network detected');
        this.logVisualAidError('Visual aid loading - slow network detected.');
      }
    }
    if (!navigator.onLine) {
      console.log('Visual aid failed to load due to no internet connection.');
      this.logVisualAidError(
        'Visual aid failed to load due to no internet connection.'
      );
      window.addEventListener('online', () => this.preloadImages(), {
        once: true,
      });
      return;
    }
  }

  next() {
    if (
      this.currentPage + 1 === this.imgArray.length ||
      this.imgArray.length === 0
    )
      return;
    this.playerService?.handleSendUserResponseVisualAid(
      'page-' + (this.currentPage + 1) + ' | '
    );
    this.currentPage += 1;
    this.currentPageChanged.emit(this.currentPage);
  }

  previous() {
    if (this.currentPage === 0) return;
    this.playerService?.handleSendUserResponseVisualAid(
      'page-' + (this.currentPage + 1) + ' | '
    );
    this.currentPage -= 1;
    this.currentPageChanged.emit(this.currentPage);
  }

  get isLoading(): boolean {
    return !this.imgArray || this.imgArray.length === 0;
  }

  logVisualAidError(errorMessage: string) {
    const userUuid = localStorage.getItem('user_uuid');
    const visualAidId = this.visualAid?.uuid || '';
    const browserInfo = navigator.userAgent;
    // SAFE: browser UA string check
    const ua = navigator.userAgent.toLowerCase();
    const isIpadOld = ua.includes('ipad');
    const isIpadNew = /macintosh/.test(ua) && 'ontouchend' in document;
    const deviceType =
      isIpadNew || isIpadOld
        ? 'iPad'
        : /Mobi|Android|iPhone/i.test(ua)
        ? 'Mobile'
        : 'Desktop';

    const sentryTagsPayload = {
      browser_info: browserInfo,
      device_type: deviceType,
      user_uuid: userUuid ?? '',
      visual_aid_id: visualAidId,
    };

    this.logger.error('[CALL] visual-aid-v2 error', new Error(errorMessage), undefined, sentryTagsPayload);
  }
}
